// components/SmartTrainer/TabRouter.tsx
import React, { useEffect, useMemo, useRef } from "react";
import { AnalysisResult, ActionableAdvice } from "../../contracts/scoring";
import { CoachingTabHeader } from "../CoachingTabHeader";
import { getLockReason } from "../../policy/uiDataPolicy";
import { getTimingToleranceMs, GLOBAL_SEVERITY_MATRIX } from "../../services/severityEngine";
import { METRIC_GOVERNANCE_POLICY } from "../../services/tabUnlockPolicy";
import { UI_METRIC_CONTRACT } from "../../policy/uiDataPolicy";

// TABS
import { CleanlinessTab } from "../../tabs/cleanliness/CleanlinessTab";
import { RhythmTab } from "../../tabs/rhythm/RhythmTab";
import { HarmonyTab } from "../../tabs/harmony/HarmonyTab";
import { PostureTab } from "../../tabs/posture/PostureTab";
import { ToneTab } from "../../tabs/tone/ToneTab";
import { ArticulationTab } from "../../tabs/articulation/ArticulationTab";
import { NuanceTab } from "../../tabs/nuance/NuanceTab";
import { FinesseTab } from "../../tabs/finesse/FinesseTab";
import { AudioTab } from "../../tabs/audio/AudioTab";

interface TabRouterProps {
  activeTab: string;
  result: AnalysisResult;
  currentLevelId: number;
  lang: "en" | "fr";
  diagnostics: ActionableAdvice[];
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  activeMeasure: number;
  onMeasureSelect: (idx: number) => void;
  beatsPerBar: number;
  isVisionAnalyzing: boolean;
}

export const TabRouter: React.FC<TabRouterProps> = (props) => {
  const {
    activeTab,
    result,
    currentLevelId,
    lang,
    diagnostics,
    isFullscreen,
    onToggleFullscreen,
    activeMeasure,
    onMeasureSelect,
    beatsPerBar,
  } = props;

  /**
   * 🔒 SINGLE SOURCE OF TRUTH (NO=100%)
   * - visibilité: viewPolicy
   * - lock/data proof: uiDataPolicy.getLockReason
   *
   * AUCUN autre “data gate” local ne doit exister ici.
   */
  const lockReason = getLockReason(activeTab, result);

  // ✅ Désactivation du MODULE (pas des bandeaux)
  const shouldDisableModule =
    lockReason === "LEVEL_LOCKED" || lockReason === "CALIBRATION_REQUIRED";

  // ✅ NO=100% : on veut afficher un panneau explicatif, lisible, scrollable.
  // Donc on ne le met PAS en pointer-events-none.
  const isNo100Missing = lockReason === "MISSING_DSP_METRICS";

  /**
   * ✅ DEBUG SAFE (NO-JSX)
   * - Ne jamais mettre un console.log dans le JSX.
   * - Déduplique les logs pour éviter un spam si `result` est recréé à chaque render.
   */
  const lastLogKeyRef = useRef<string>("");

  useEffect(() => {
    try {
      const dbg: any = result?.session?.metrics?.debug;
      const policy: any = (METRIC_GOVERNANCE_POLICY as any)?.[activeTab];

      const sessionKey =
        (result as any)?.session?.id ||
        (result as any)?.audit_trail?.scoring_ruleset_id ||
        (result as any)?.audit_trail?.analysis_hash ||
        "no_session_key";

      const logKey = `${sessionKey}::${activeTab}::${lockReason}`;
      if (lastLogKeyRef.current === logKey) return;
      lastLogKeyRef.current = logKey;

      const len = (a: any) => (Array.isArray(a) ? a.length : 0);

            const validatorType = typeof (UI_METRIC_CONTRACT as any)?.[activeTab];
      const hasContract = validatorType === "function";

      console.log("[LOCK-DEBUG]", {
        activeTab,
        lockReason,
        coaching_level: result?.coaching_level,
        minLevel: policy?.minLevel,
        requiresCalibration: policy?.requiresCalibration,
        calibration_status: result?.session?.truth_report?.calibration_status,
        origin: result?.session?.truth_report?.tech_pack?.origin,
        contract: { hasContract, validatorType },
        has: {
          dbg_timingDeviationMs_len: len(dbg?.timingDeviationMs),
          dbg_onsetTimesMs_len: len(dbg?.onsetTimesMs),
          dbg_stability: dbg?.stability,
          dbg_transient_ratio: dbg?.transient_ratio,
          dbg_nuance_measurable: dbg?.nuance?.measurable,
          dbg_relativeDynamics: dbg?.nuance?.relativeDynamics,
          dbg_rms_db: dbg?.rms_db,

          rhythm_stuPeaks_len: len((result as any)?.rhythm?.stuPeaks),
          rhythm_stuEnvelope_len: len((result as any)?.rhythm?.stuEnvelope),

          metrics_rhythm: result?.session?.metrics?.rhythm,
          metrics_harmony: result?.session?.metrics?.harmony,
          metrics_nuance: result?.session?.metrics?.nuance,
          metrics_tone: result?.session?.metrics?.tone,
          metrics_articulation: result?.session?.metrics?.articulation,
          metrics_posture: result?.session?.metrics?.posture,
          metrics_cleanliness: result?.session?.metrics?.cleanliness,
          quality_score: result?.session?.quality_score,

          tone_details: Boolean((result as any)?.tone?.details),
          tone_mfcc_len: len((result as any)?.tone?.mfccData),
          ghosts_isArray: Array.isArray((result as any)?.tone?.ghostNotes),

          biomechanics_present:
            (result as any)?.biomechanics !== null &&
            (result as any)?.biomechanics !== undefined,
        },
      });
    } catch (e) {
      console.warn("[LOCK-DEBUG] failed", e);
    }
  }, [activeTab, lockReason, result]);

  const renderContent = () => {
    // Si la donnée DSP manque => panneau explicatif (NO=100%)
    if (isNo100Missing) {
      return (
        <div className="p-16 text-center rounded-[48px] bg-black/40 border border-white/10 animate-fade-in">
          <p className="text-xs font-black uppercase tracking-widest text-orange-400">
            Données DSP absentes (NO=100%)
          </p>
          <p className="text-[10px] mt-2 italic text-slate-400">
            Ce module est visible mais désactivé : la preuve DSP requise n’a pas été produite pour cette analyse.
          </p>
          <p className="text-[10px] mt-2 italic text-slate-500">
            Action : relancer une analyse avec un signal plus propre (attaque nette / volume stable / silence réduit).
          </p>
        </div>
      );
    }

    try {
      switch (activeTab) {
        case "audio":
          return <AudioTab result={result} />;

        case "rhythm":
          return <RhythmTab result={result} lang={lang} isFullscreen={isFullscreen} />;

        case "synchronization":
          return <FinesseTab result={result} currentLevelId={currentLevelId} />;

        case "posture":
          return (
            <PostureTab
              result={result}
              isFullscreen={isFullscreen}
              onToggleFullscreen={onToggleFullscreen}
            />
          );

        case "harmony":
          return (
            <HarmonyTab
              result={result}
              activeMeasure={activeMeasure}
              onMeasureSelect={onMeasureSelect}
              beatsPerBar={beatsPerBar}
            />
          );

        case "tone":
          return <ToneTab result={result} lang={lang} currentLevelId={currentLevelId} />;

        case "cleanliness":
          return <CleanlinessTab result={result} />;

        case "articulation":
          return <ArticulationTab result={result} />;

        case "nuance":
          return <NuanceTab result={result} />;

        default:
          return null;
      }
    } catch (e) {
      console.error("[TAB-ROUTER] Crash caught:", e);
      return (
        <div className="p-20 text-center bg-red-900/10 border border-red-900/40 rounded-[48px]">
          <p className="text-red-400 font-black uppercase text-xs tracking-widest">
            Critical Module Error
          </p>
          <p className="text-[10px] mt-2 italic text-slate-500">
            Le rendu de ce module a échoué. Veuillez relancer une analyse.
          </p>
        </div>
      );
    }
  };

  const getAuditInfo = () => {
    const bpm = result.session?.bpm || 90;
    const tol = getTimingToleranceMs(currentLevelId, bpm);
    const dbg = (result.session?.metrics?.debug || {}) as any;

    switch (activeTab) {
      case "rhythm":
        return { measured: Math.round(result.session?.metrics?.timingErrorMs ?? 0), threshold: tol, unit: "ms" };

      case "cleanliness": {
        const durationInMin = Math.max(0.01, (result.session?.duration || 0) / 60);
        const ghosts = (result as any)?.tone?.ghostNotes?.length || 0;
        const gpm = Math.round(ghosts / durationInMin);

        const threshold = GLOBAL_SEVERITY_MATRIX?.[currentLevelId]?.cleanliness?.maxGhostsPerMin ?? 25;
        return { measured: gpm, threshold, unit: "err/min" };
      }

      case "harmony":
        return { measured: result.session?.metrics?.harmony ?? 0, threshold: 75, unit: "%" };

      case "synchronization":
        return { measured: Math.round((dbg.stability || 0) * 100), threshold: 80, unit: "%" };

      case "articulation":
        return { measured: Math.round((dbg.transient_ratio || 0) * 100), threshold: 50, unit: "%" };

      case "nuance":
        return { measured: result.session?.metrics?.nuance ?? 0, threshold: 80, unit: "%" };

      case "posture":
        return { measured: result.session?.metrics?.posture ?? 0, threshold: 75, unit: "%" };

      case "tone":
        return { measured: result.session?.metrics?.tone ?? 0, threshold: 80, unit: "%" };

      case "audio":
        return { measured: (result as any)?.session?.quality_score || 0, threshold: 75, unit: "%" };

      default:
        return null;
    }
  };

  const domainMap: Record<string, any> = {
    rhythm: "TIMING",
    harmony: "HARMONY",
    nuance: "NUANCE",
    posture: "POSTURE",
    tone: "TONE",
    cleanliness: "CLEANLINESS",
    articulation: "ATTAQUE",
    synchronization: "STABILITY",
    audio: "GENERAL",
  };

  const headerTitleMap: Record<string, string> = {
    rhythm: "RYTHME MATCH",
    harmony: "ACCORDS TRACKER",
    nuance: "NUANCES UNIT",
    posture: "VISION LAB",
    tone: "TIMBRE FIXER",
    cleanliness: "PROPRETÉ INSPECTOR",
    articulation: "ATTAQUE FLOW",
    synchronization: "FINESSE GAUGE",
    audio: "AUDIO PROBES",
  };

  const adv = useMemo(() => (Array.isArray(diagnostics) ? diagnostics : []), [diagnostics]);

  return (
    <div className={isFullscreen ? "h-full flex flex-col" : "max-w-7xl mx-auto space-y-4"}>
      <CoachingTabHeader
        title={headerTitleMap[activeTab] || "ANALYSE"}
        domain={domainMap[activeTab] || "GENERAL"}
        advices={adv.filter((d) => d.domain === domainMap[activeTab])}
        score={null}
        onToggleFullscreen={onToggleFullscreen}
        isFullscreen={isFullscreen}
        auditInfo={getAuditInfo() ?? undefined}

      />

      {lockReason === "CALIBRATION_REQUIRED" && (
        <div className="mx-4 mb-6 p-5 rounded-[24px] border-2 border-orange-500/40 bg-orange-500/10 text-orange-400">
          <p className="text-xs font-black uppercase tracking-widest">Précision non calibrée</p>
          <p className="text-[10px] italic opacity-80">Calibration requise pour garantir la précision temporelle.</p>
        </div>
      )}

      {lockReason === "LEVEL_LOCKED" && (
        <div className="mx-4 mb-6 p-5 rounded-[24px] border-2 border-white/10 bg-white/5 text-slate-400">
          <p className="text-xs font-black uppercase tracking-widest">Contenu verrouillé</p>
          <p className="text-[10px] italic opacity-80">
            Ce module se débloque au niveau {(METRIC_GOVERNANCE_POLICY as any)?.[activeTab]?.minLevel}.
          </p>
        </div>
      )}

      {lockReason === "MISSING_DSP_METRICS" && (
        <div className="mx-4 mb-6 p-5 rounded-[24px] border-2 border-orange-500/30 bg-orange-500/5 text-orange-300">
          <p className="text-xs font-black uppercase tracking-widest">NO=100% — Module désactivé</p>
          <p className="text-[10px] italic opacity-80">
            L’onglet est visible, mais la métrique DSP requise est absente. Aucun fallback n’est autorisé.
          </p>
        </div>
      )}

      {/* ✅ Important: on ne désactive QUE si LEVEL/CALIB, pas si NO=100% missing (car on veut lire le panneau). */}
      <div className={`flex-1 ${shouldDisableModule ? "opacity-40 grayscale pointer-events-none" : ""}`}>
        {renderContent()}
      </div>
    </div>
  );
};
